import React from 'react';

const TermsOfService: React.FC = () => {
  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Terms of Service</h1>
        <p className="page-subtitle">Legal terms and conditions for using Flow</p>
        <p className="last-updated">Last updated: January 15, 2025</p>
      </div>

      <div className="page-content legal-content">
        <section className="content-section">
          <h2>1. Acceptance of Terms</h2>
          <p>
            By accessing and using Flow ("the Service"), you accept and agree to be bound by the terms
            and provision of this agreement. If you do not agree to abide by the above, please do not
            use this service.
          </p>
        </section>

        <section className="content-section">
          <h2>2. Description of Service</h2>
          <p>
            Flow is a web-based writing platform that allows users to create, edit, collaborate on,
            and publish written content. The Service includes features such as text editing, collaboration
            tools, publishing capabilities, and user profile management.
          </p>
        </section>

        <section className="content-section">
          <h2>3. User Accounts</h2>

          <h3>3.1 Account Creation</h3>
          <p>
            To use certain features of Flow, you must create an account. You agree to provide accurate,
            current, and complete information during registration and to update such information to keep it accurate.
          </p>

          <h3>3.2 Account Security</h3>
          <p>
            You are responsible for maintaining the confidentiality of your account credentials and for all
            activities that occur under your account. You must notify us immediately of any unauthorized
            use of your account.
          </p>

          <h3>3.3 Account Termination</h3>
          <p>
            We reserve the right to suspend or terminate your account at any time for violations of these terms.
            You may also delete your account at any time through your account settings.
          </p>
        </section>

        <section className="content-section">
          <h2>4. User Content and Conduct</h2>

          <h3>4.1 Content Ownership</h3>
          <p>
            You retain ownership of all content you create and publish on Flow. By using our Service,
            you grant us a limited, non-exclusive license to store, display, and distribute your content
            as necessary to provide the Service.
          </p>

          <h3>4.2 Content Responsibility</h3>
          <p>
            You are solely responsible for the content you create, share, and publish on Flow. You represent
            and warrant that you have all necessary rights to your content and that it does not violate any laws or rights of third parties.
          </p>

          <h3>4.3 Prohibited Content</h3>
          <p>You agree not to use Flow to create, share, or publish content that:</p>
          <ul>
            <li>Is illegal, harmful, threatening, abusive, or defamatory</li>
            <li>Infringes on intellectual property rights</li>
            <li>Contains hate speech, discrimination, or harassment</li>
            <li>Promotes violence or illegal activities</li>
            <li>Contains spam, malware, or phishing attempts</li>
            <li>Violates privacy rights of others</li>
            <li>Contains false or misleading information presented as fact</li>
          </ul>
        </section>

        <section className="content-section">
          <h2>5. Acceptable Use</h2>
          <p>You agree to use Flow only for lawful purposes and in accordance with these Terms. You agree not to:</p>
          <ul>
            <li>Violate any applicable laws or regulations</li>
            <li>Impersonate others or create false identities</li>
            <li>Interfere with or disrupt the Service or servers</li>
            <li>Attempt to gain unauthorized access to any part of the Service</li>
            <li>Use automated tools to access or interact with the Service without permission</li>
            <li>Sell, transfer, or sublicense your access to the Service</li>
          </ul>
        </section>

        <section className="content-section">
          <h2>6. Intellectual Property</h2>

          <h3>6.1 Flow's Rights</h3>
          <p>
            The Service and its original content, features, and functionality are and will remain the
            exclusive property of Flow and its licensors. The Service is protected by copyright,
            trademark, and other laws.
          </p>

          <h3>6.2 User Rights</h3>
          <p>
            Subject to these Terms, we grant you a limited, non-exclusive, non-transferable license
            to access and use the Service for your personal or business purposes.
          </p>
        </section>

        <section className="content-section">
          <h2>7. Privacy</h2>
          <p>
            Your privacy is important to us. Please review our Privacy Policy, which also governs
            your use of the Service, to understand our practices regarding the collection and use
            of your information.
          </p>
        </section>

        <section className="content-section">
          <h2>8. Payments and Subscriptions</h2>

          <h3>8.1 Paid Services</h3>
          <p>
            Some features of Flow may require payment. Pricing for paid services will be clearly
            displayed before purchase. All payments are processed securely through third-party payment processors.
          </p>

          <h3>8.2 Billing</h3>
          <p>
            For subscription services, you will be billed in advance on a recurring basis. You are
            responsible for providing current, complete, and accurate billing information.
          </p>

          <h3>8.3 Refunds</h3>
          <p>
            Refund policies will be clearly stated at the time of purchase. Generally, payments for
            subscription services are non-refundable, but we may provide refunds at our discretion.
          </p>
        </section>

        <section className="content-section">
          <h2>9. Service Availability</h2>
          <p>
            While we strive to maintain high availability, we do not guarantee that the Service will
            be available at all times. We may experience downtime for maintenance, updates, or due to
            circumstances beyond our control.
          </p>
        </section>

        <section className="content-section">
          <h2>10. Content Moderation</h2>
          <p>
            We reserve the right to review, moderate, or remove content that violates these Terms or
            our Community Guidelines. We may take action against users who violate our policies,
            including warnings, content removal, or account suspension.
          </p>
        </section>

        <section className="content-section">
          <h2>11. Disclaimers and Limitation of Liability</h2>

          <h3>11.1 Service "As Is"</h3>
          <p>
            The Service is provided "as is" and "as available" without warranties of any kind, either
            express or implied, including but not limited to warranties of merchantability, fitness
            for a particular purpose, or non-infringement.
          </p>

          <h3>11.2 Limitation of Liability</h3>
          <p>
            To the maximum extent permitted by law, Flow shall not be liable for any indirect,
            incidental, special, consequential, or punitive damages, or any loss of profits or
            revenues, whether incurred directly or indirectly.
          </p>
        </section>

        <section className="content-section">
          <h2>12. Indemnification</h2>
          <p>
            You agree to defend, indemnify, and hold harmless Flow and its officers, directors,
            employees, and agents from and against any claims, damages, obligations, losses,
            liabilities, costs, or debt arising from your use of the Service or violation of these Terms.
          </p>
        </section>

        <section className="content-section">
          <h2>13. Governing Law</h2>
          <p>
            These Terms shall be interpreted and governed by the laws of the jurisdiction in which
            Flow operates, without regard to conflict of law provisions. Any disputes shall be
            resolved in the appropriate courts of that jurisdiction.
          </p>
        </section>

        <section className="content-section">
          <h2>14. Changes to Terms</h2>
          <p>
            We reserve the right to modify or replace these Terms at any time. If a revision is material,
            we will try to provide at least 30 days notice prior to any new terms taking effect.
            Your continued use of the Service after changes become effective constitutes acceptance of the new Terms.
          </p>
        </section>

        <section className="content-section">
          <h2>15. Severability</h2>
          <p>
            If any provision of these Terms is held to be unenforceable or invalid, such provision will
            be changed and interpreted to accomplish the objectives of such provision to the greatest
            extent possible under applicable law, and the remaining provisions will continue in full force and effect.
          </p>
        </section>

        <section className="content-section">
          <h2>16. Contact Information</h2>
          <p>
            If you have any questions about these Terms of Service, please contact us:
          </p>
          <ul>
            <li>Email: legal@flow-writing.com</li>
            <li>Through our <a href="/contact">Contact Us</a> page</li>
          </ul>
        </section>
      </div>
    </div>
  );
};

export default TermsOfService;